import { Component, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormArray, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { BaseAuthComponent } from '../../../shared/common/BaseAuthComponent.class';
import { PageTitleService, ToastService } from '../../../shared/services';
import { ScheduleService, BusinessScheduleRange } from '../../../services/business/schedule.service';

// Ordered Monday-first, which is how most businesses think about their week
const DAYS_OF_WEEK = [
  { value: 1, label: 'Lunes', short: 'Lun' },
  { value: 2, label: 'Martes', short: 'Mar' },
  { value: 3, label: 'Miércoles', short: 'Mié' },
  { value: 4, label: 'Jueves', short: 'Jue' },
  { value: 5, label: 'Viernes', short: 'Vie' },
  { value: 6, label: 'Sábado', short: 'Sáb' },
  { value: 0, label: 'Domingo', short: 'Dom' },
];

const TIME_PATTERN = /^([0-1][0-9]|2[0-3]):[0-5][0-9]$/;
const DEFAULT_START = '09:00';
const DEFAULT_END = '18:00';

interface TimelineSegment {
  left: number;
  width: number;
  label: string;
}

@Component({
  selector: 'app-business-hours',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './business-hours.html',
  styleUrl: './business-hours.css',
})
export class BusinessHours extends BaseAuthComponent implements OnInit {
  private fb = inject(FormBuilder);
  private scheduleService = inject(ScheduleService);
  private toastService = inject(ToastService);
  titleService = inject(PageTitleService);

  isLoading = signal(true);
  isSaving = signal(false);
  hasDefaultSchedules = signal(false);
  daysOfWeek = DAYS_OF_WEEK;

  // "Copiar a…" popover state
  copySourceDay = signal<number | null>(null);
  copyTargets = signal<Set<number>>(new Set());

  form = this.fb.group({
    schedules: this.fb.array<FormGroup>([]),
  });

  get schedulesArray(): FormArray {
    return this.form.get('schedules') as FormArray;
  }

  ngOnInit(): void {
    this.titleService.setPage({
      title: 'Horarios del negocio',
      subtitle: 'Días y horas en que recibes citas, para todas tus agendas',
      breadcrumbs: [{ name: 'Configuración', url: '/settings' }],
    });
    this.loadSchedule();
  }

  loadSchedule(): void {
    this.isLoading.set(true);
    this.closeCopy();
    this.scheduleService.getBusinessSchedule(this.businessId).subscribe({
      next: (response) => {
        this.schedulesArray.clear();

        // If no schedules exist, load default schedules
        if (!response.timeRanges || response.timeRanges.length === 0) {
          this.loadDefaultSchedules();
          this.isLoading.set(false);
          return;
        }

        this.hasDefaultSchedules.set(false);

        // Group schedules by day of week
        const schedulesByDay = new Map<number, BusinessScheduleRange[]>();
        response.timeRanges.forEach((range) => {
          if (!schedulesByDay.has(range.dayOfWeek)) {
            schedulesByDay.set(range.dayOfWeek, []);
          }
          schedulesByDay.get(range.dayOfWeek)!.push(range);
        });

        DAYS_OF_WEEK.forEach((day) => {
          const daySchedules = (schedulesByDay.get(day.value) || []).sort((a, b) =>
            a.startTime.localeCompare(b.startTime)
          );
          if (daySchedules.length > 0) {
            daySchedules.forEach((schedule) => {
              this.addScheduleRange(day.value, schedule.startTime?.slice(0, 5), schedule.endTime?.slice(0, 5), schedule.isActive);
            });
          } else {
            // Closed days keep one placeholder range so they can be reopened
            this.addScheduleRange(day.value, '', '', false);
          }
        });

        this.form.markAsPristine();
        this.isLoading.set(false);
      },
      error: (err) => {
        // If 404 or no schedules, load defaults
        if (err.status === 404 || err.status === 0) {
          this.loadDefaultSchedules();
          this.isLoading.set(false);
        } else {
          console.error('Error cargando horarios:', err);
          this.toastService.show('No pudimos cargar los horarios del negocio', 'warning');
          this.isLoading.set(false);
        }
      },
    });
  }

  private loadDefaultSchedules(): void {
    this.schedulesArray.clear();

    // Default schedule: Monday to Friday, 9:00 to 18:00; weekend closed
    DAYS_OF_WEEK.forEach((day) => {
      const isWeekday = day.value >= 1 && day.value <= 5;
      this.addScheduleRange(day.value, isWeekday ? DEFAULT_START : '', isWeekday ? DEFAULT_END : '', isWeekday);
    });

    this.hasDefaultSchedules.set(true);
    // Suggested hours are not saved yet, so surface the save bar
    this.form.markAsDirty();
  }

  addScheduleRange(dayOfWeek: number, startTime: string = '', endTime: string = '', isActive: boolean = true): void {
    const scheduleGroup = this.fb.group({
      dayOfWeek: [dayOfWeek, Validators.required],
      startTime: [startTime],
      endTime: [endTime],
      isActive: [isActive],
    });

    this.applyTimeValidators(scheduleGroup, isActive);
    scheduleGroup.get('isActive')?.valueChanges.subscribe((active) => {
      this.applyTimeValidators(scheduleGroup, !!active);
    });

    this.schedulesArray.push(scheduleGroup);
  }

  private applyTimeValidators(group: FormGroup, active: boolean): void {
    const startTimeControl = group.get('startTime');
    const endTimeControl = group.get('endTime');

    if (active) {
      startTimeControl?.setValidators([Validators.required, Validators.pattern(TIME_PATTERN)]);
      endTimeControl?.setValidators([Validators.required, Validators.pattern(TIME_PATTERN)]);
    } else {
      startTimeControl?.clearValidators();
      endTimeControl?.clearValidators();
    }

    startTimeControl?.updateValueAndValidity({ emitEvent: false });
    endTimeControl?.updateValueAndValidity({ emitEvent: false });
  }

  getSchedulesForDay(dayOfWeek: number): Array<{ group: FormGroup; index: number }> {
    return this.schedulesArray.controls
      .map((control, index) => ({ group: control as FormGroup, index }))
      .filter(({ group }) => group.get('dayOfWeek')?.value === dayOfWeek);
  }

  getDayName(dayOfWeek: number): string {
    return DAYS_OF_WEEK.find((d) => d.value === dayOfWeek)?.label || '';
  }

  // ---- Day-level actions ----

  isDayOpen(dayOfWeek: number): boolean {
    return this.getSchedulesForDay(dayOfWeek).some(({ group }) => group.get('isActive')?.value);
  }

  toggleDay(dayOfWeek: number): void {
    const ranges = this.getSchedulesForDay(dayOfWeek);
    const open = !this.isDayOpen(dayOfWeek);

    if (ranges.length === 0) {
      this.addScheduleRange(dayOfWeek, DEFAULT_START, DEFAULT_END, true);
    } else {
      ranges.forEach(({ group }) => {
        // Reopening a day with no hours suggests a typical schedule instead of blank fields
        if (open && !group.get('startTime')?.value && !group.get('endTime')?.value) {
          group.patchValue({ startTime: DEFAULT_START, endTime: DEFAULT_END });
        }
        group.get('isActive')?.setValue(open);
      });
    }
    this.form.markAsDirty();
  }

  addRangeForDay(dayOfWeek: number): void {
    const ranges = this.getSchedulesForDay(dayOfWeek).filter(({ group }) => group.get('isActive')?.value);
    const lastEnd = ranges.length ? this.toMinutes(ranges[ranges.length - 1].group.get('endTime')?.value) : null;

    // Start the new range one hour after the last one ends, two hours long
    let start = lastEnd !== null ? Math.min(lastEnd + 60, 22 * 60) : 9 * 60;
    let end = Math.min(start + 120, 23 * 60 + 59);
    if (end <= start) {
      start = 9 * 60;
      end = 11 * 60;
    }
    this.addScheduleRange(dayOfWeek, this.fromMinutes(start), this.fromMinutes(end), true);
    this.form.markAsDirty();
  }

  removeScheduleRange(index: number): void {
    const group = this.schedulesArray.at(index) as FormGroup;
    const dayOfWeek = group.get('dayOfWeek')?.value;
    const activeForDay = this.getSchedulesForDay(dayOfWeek).filter(({ group: g }) => g.get('isActive')?.value);

    // Removing the only range closes the day rather than leaving it empty
    if (activeForDay.length <= 1) {
      group.get('isActive')?.setValue(false);
    } else {
      this.schedulesArray.removeAt(index);
    }
    this.form.markAsDirty();
  }

  // ---- Copy to other days ----

  openCopy(dayOfWeek: number): void {
    if (this.copySourceDay() === dayOfWeek) {
      this.closeCopy();
      return;
    }
    this.copySourceDay.set(dayOfWeek);
    this.copyTargets.set(new Set());
  }

  closeCopy(): void {
    this.copySourceDay.set(null);
    this.copyTargets.set(new Set());
  }

  toggleCopyTarget(dayOfWeek: number): void {
    const next = new Set(this.copyTargets());
    next.has(dayOfWeek) ? next.delete(dayOfWeek) : next.add(dayOfWeek);
    this.copyTargets.set(next);
  }

  selectCopyTargets(kind: 'weekdays' | 'all'): void {
    const source = this.copySourceDay();
    const days = DAYS_OF_WEEK.map((d) => d.value).filter(
      (v) => v !== source && (kind === 'all' || (v >= 1 && v <= 5))
    );
    this.copyTargets.set(new Set(days));
  }

  applyCopy(): void {
    const sourceDay = this.copySourceDay();
    const targets = [...this.copyTargets()];
    if (sourceDay === null || targets.length === 0) return;

    const sourceData = this.getSchedulesForDay(sourceDay)
      .map(({ group }) => group.value as { startTime: string; endTime: string; isActive: boolean })
      .filter((r) => r.isActive);

    for (const day of targets) {
      const existingIndexes = this.getSchedulesForDay(day)
        .map((item) => item.index)
        .sort((a, b) => b - a); // Remove from end to preserve indexes
      for (const idx of existingIndexes) {
        this.schedulesArray.removeAt(idx);
      }

      if (sourceData.length === 0) {
        this.addScheduleRange(day, '', '', false);
      } else {
        for (const data of sourceData) {
          this.addScheduleRange(day, data.startTime, data.endTime, true);
        }
      }
    }

    this.form.markAsDirty();
    const count = targets.length;
    this.toastService.show(
      `Horario de ${this.getDayName(sourceDay)} copiado a ${count} ${count === 1 ? 'día' : 'días'}`,
      'success'
    );
    this.closeCopy();
  }

  // ---- Inline validation ----

  getRangeError(dayOfWeek: number, index: number): string | null {
    const group = this.schedulesArray.at(index) as FormGroup;
    if (!group?.get('isActive')?.value) return null;

    const start = this.toMinutes(group.get('startTime')?.value);
    const end = this.toMinutes(group.get('endTime')?.value);
    if (start === null || end === null) return 'Completa la hora de apertura y de cierre';
    if (end <= start) return 'La hora de cierre debe ser posterior a la de apertura';

    const overlaps = this.getSchedulesForDay(dayOfWeek).some(({ group: other, index: otherIndex }) => {
      if (otherIndex === index || !other.get('isActive')?.value) return false;
      const oStart = this.toMinutes(other.get('startTime')?.value);
      const oEnd = this.toMinutes(other.get('endTime')?.value);
      return oStart !== null && oEnd !== null && start < oEnd && oStart < end;
    });
    return overlaps ? 'Este horario se cruza con otro del mismo día' : null;
  }

  hasRangeErrors(): boolean {
    return this.schedulesArray.controls.some((c, i) => !!this.getRangeError(c.get('dayOfWeek')?.value, i));
  }

  // ---- Week overview ----

  private activeRanges(dayOfWeek: number): Array<{ start: number; end: number }> {
    return this.getSchedulesForDay(dayOfWeek)
      .filter(({ group }) => group.get('isActive')?.value)
      .map(({ group }) => ({
        start: this.toMinutes(group.get('startTime')?.value),
        end: this.toMinutes(group.get('endTime')?.value),
      }))
      .filter((r): r is { start: number; end: number } => r.start !== null && r.end !== null && r.end > r.start);
  }

  /** Visible hour window for the overview: fits the week's hours, never narrower than 8:00–20:00. */
  get timelineBounds(): { start: number; end: number } {
    const all = DAYS_OF_WEEK.flatMap((d) => this.activeRanges(d.value));
    const minStart = Math.min(8 * 60, ...all.map((r) => r.start));
    const maxEnd = Math.max(20 * 60, ...all.map((r) => r.end));
    return { start: Math.floor(minStart / 60) * 60, end: Math.min(Math.ceil(maxEnd / 60) * 60, 24 * 60) };
  }

  get timelineTicks(): Array<{ left: number; label: string }> {
    const { start, end } = this.timelineBounds;
    const span = end - start;
    const step = span > 14 * 60 ? 240 : 120;
    const ticks: Array<{ left: number; label: string }> = [];
    for (let m = Math.ceil(start / step) * step; m <= end; m += step) {
      ticks.push({ left: ((m - start) / span) * 100, label: `${m / 60}:00` });
    }
    return ticks;
  }

  getTimelineSegments(dayOfWeek: number): TimelineSegment[] {
    const { start, end } = this.timelineBounds;
    const span = end - start;
    return this.activeRanges(dayOfWeek).map((r) => ({
      left: ((r.start - start) / span) * 100,
      width: ((r.end - r.start) / span) * 100,
      label: `${this.fromMinutes(r.start)} a ${this.fromMinutes(r.end)}`,
    }));
  }

  getDayMinutes(dayOfWeek: number): number {
    return this.activeRanges(dayOfWeek).reduce((sum, r) => sum + (r.end - r.start), 0);
  }

  get weekSummary(): { openDays: number; hours: string } {
    const openDays = DAYS_OF_WEEK.filter((d) => this.isDayOpen(d.value)).length;
    const minutes = DAYS_OF_WEEK.reduce((sum, d) => sum + this.getDayMinutes(d.value), 0);
    return { openDays, hours: this.formatDuration(minutes) };
  }

  formatDuration(minutes: number): string {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return m ? `${h} h ${m} min` : `${h} h`;
  }

  scrollToDay(dayOfWeek: number): void {
    document.getElementById(`bh-day-${dayOfWeek}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  private toMinutes(value: string | null | undefined): number | null {
    if (!value || !TIME_PATTERN.test(value.slice(0, 5))) return null;
    const [h, m] = value.split(':').map(Number);
    return h * 60 + m;
  }

  private fromMinutes(total: number): string {
    const h = Math.floor(total / 60);
    const m = total % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  }

  discardChanges(): void {
    this.loadSchedule();
  }

  saveSchedule(): void {
    const schedules = this.schedulesArray.value;

    const errorIndex = this.schedulesArray.controls.findIndex(
      (c, i) => !!this.getRangeError(c.get('dayOfWeek')?.value, i)
    );
    if (errorIndex >= 0) {
      const day = this.schedulesArray.at(errorIndex).get('dayOfWeek')?.value;
      this.form.markAllAsTouched();
      this.toastService.show(`Revisa el horario del ${this.getDayName(day).toLowerCase()}`, 'warning');
      this.scrollToDay(day);
      return;
    }

    // Filter out empty schedules and inactive ones
    const timeRanges = schedules
      .filter((s: any) => s.startTime && s.endTime && s.isActive)
      .map((s: any) => ({
        dayOfWeek: s.dayOfWeek,
        startTime: s.startTime,
        endTime: s.endTime,
        isActive: s.isActive,
      }));

    if (timeRanges.length === 0) {
      this.toastService.show('Abre al menos un día para guardar tus horarios', 'warning');
      return;
    }

    this.isSaving.set(true);
    this.scheduleService
      .createBusinessSchedule({
        businessId: this.businessId,
        timeRanges,
      })
      .subscribe({
        next: () => {
          this.isSaving.set(false);
          this.hasDefaultSchedules.set(false);
          this.toastService.show('Horarios guardados', 'success');
          this.loadSchedule();
        },
        error: (err) => {
          this.isSaving.set(false);
          console.error('Error guardando horarios:', err);
          this.toastService.show('No pudimos guardar los horarios. Inténtalo de nuevo.', 'warning');
        },
      });
  }
}
